import { AdditionalQuestion, QuestionType } from '../../types/guest';
import { useAdditionalQuestionsStore } from '@/stores/additionalQuestionsStore/additionalQuestionsStore';

type AdditionalQuestionAnswer = string | number | boolean | (string | number | boolean)[] | undefined;

interface GuestData {
  id?: string;
  name?: string;
  surname?: string;
  presence?: boolean;
  connection?: string;
  connectedGuestId?: string;
  tableNumber?: number;
  tableAdditionalInfo?: string;
  tableLayoutInfo?: unknown;
  seatIndex?: number;
  additionalQuestions?: AdditionalQuestion[] | string;
  [key: string]: unknown;
}

const STANDARD_GUEST_KEYS = [
  'id',
  'name',
  'surname',
  'presence',
  'connection',
  'connectedGuestId',
  'tableNumber',
  'tableAdditionalInfo',
  'tableLayoutInfo',
  'seatIndex',
  'additionalQuestions',
];

const inferQuestionType = (answer: AdditionalQuestionAnswer): QuestionType => {
  if (typeof answer === 'boolean') return QuestionType.BOOLEAN;
  if (Array.isArray(answer)) return QuestionType.ARRAY;
  return QuestionType.TEXT;
};

export class AdditionalQuestionsService {
  static getQuestionTemplates(): AdditionalQuestion[] {
    const store = useAdditionalQuestionsStore();
    return store.getQuestionTemplates;
  }

  static saveQuestionTemplates(questions: AdditionalQuestion[]): void {
    const store = useAdditionalQuestionsStore();
    // Ensure all questions have a type
    const questionsWithTypes = questions.map((q) => ({
      ...q,
      type: q.type || inferQuestionType(q.answer) || QuestionType.TEXT,
    }));
    store.setQuestionTemplates(questionsWithTypes);
  }

  static addQuestionTemplate(question: AdditionalQuestion): void {
    const store = useAdditionalQuestionsStore();
    // Ensure question has a type
    const questionWithType = {
      ...question,
      type: question.type || QuestionType.TEXT,
    };
    store.addQuestionTemplate(questionWithType);
  }

  static removeQuestionTemplate(questionId: string): void {
    const store = useAdditionalQuestionsStore();
    store.removeQuestionTemplate(questionId);
  }

  static updateQuestionTemplate(question: AdditionalQuestion): void {
    const store = useAdditionalQuestionsStore();
    // Ensure question has a type
    const questionWithType = {
      ...question,
      type: question.type || inferQuestionType(question.answer) || QuestionType.TEXT,
    };
    store.updateQuestionTemplate(questionWithType);
  }

  static parseGuestAdditionalQuestions(guestData: GuestData): AdditionalQuestion[] {
    if (Array.isArray(guestData.additionalQuestions) && guestData.additionalQuestions.length > 0) {
      return guestData.additionalQuestions.map((q) => ({
        ...q,
        type: q.type || inferQuestionType(q.answer) || QuestionType.TEXT,
      }));
    }

    if (typeof guestData.additionalQuestions === 'string') {
      try {
        const parsed = JSON.parse(guestData.additionalQuestions);
        return parsed.map((q: AdditionalQuestion) => ({
          ...q,
          type: q.type || inferQuestionType(q.answer) || QuestionType.TEXT,
        }));
      } catch {
        return [];
      }
    }

    // Infer from flat structure
    return Object.keys(guestData)
      .filter((key) => !STANDARD_GUEST_KEYS.includes(key))
      .map((key) => {
        const answer = guestData[key] as AdditionalQuestionAnswer;
        return {
          id: key,
          type: inferQuestionType(answer),
          answer,
        };
      });
  }

  static processAndUpdateTemplates(guestsData: GuestData[]): AdditionalQuestion[] {
    const currentTemplates = this.getQuestionTemplates();
    const allUniqueQuestions = new Map<string, AdditionalQuestion>();
    const questionTypesMap = new Map<string, Set<QuestionType>>();

    // Add current templates to the map
    currentTemplates.forEach((template) => {
      allUniqueQuestions.set(template.id, {
        ...template,
        type: template.type || QuestionType.TEXT,
      });
    });

    // Collect all types for each question across all guests
    guestsData.forEach((guest) => {
      const parsedQuestions = this.parseGuestAdditionalQuestions(guest);
      parsedQuestions.forEach((q) => {
        if (!q.id) return;

        // Initialize set of types for this question if doesn't exist
        if (!questionTypesMap.has(q.id)) {
          questionTypesMap.set(q.id, new Set());
        }

        // Add type to the set
        const inferredType = inferQuestionType(q.answer);
        questionTypesMap.get(q.id)?.add(inferredType);

        // Add question to allUniqueQuestions if not exists
        if (!allUniqueQuestions.has(q.id)) {
          allUniqueQuestions.set(q.id, {
            id: q.id,
            type: QuestionType.TEXT, // temporary type, will be updated below
            answer: '',
          });
        }
      });
    });

    // Update question types based on collected information
    for (const [questionId, types] of questionTypesMap.entries()) {
      const question = allUniqueQuestions.get(questionId);
      if (!question) continue;

      // If we have only TEXT type or empty answers, keep TEXT
      // If we have any ARRAY or BOOLEAN type (and rest are empty/text), use that type
      let finalType = QuestionType.TEXT;
      if (types.has(QuestionType.ARRAY)) {
        finalType = QuestionType.ARRAY;
      } else if (types.has(QuestionType.BOOLEAN)) {
        finalType = QuestionType.BOOLEAN;
      }

      allUniqueQuestions.set(questionId, {
        ...question,
        type: finalType,
      });
    }

    // Convert map to array and save
    const updatedTemplates = Array.from(allUniqueQuestions.values());
    this.saveQuestionTemplates(updatedTemplates);
    return updatedTemplates;
  }

  static normalizeGuestQuestions(guestData: GuestData, templates: AdditionalQuestion[]): AdditionalQuestion[] {
    const parsedQuestions = this.parseGuestAdditionalQuestions(guestData);
    const questionsMap = new Map(parsedQuestions.map((q) => [q.id, q]));

    return templates.map((template) => {
      const existingQuestion = questionsMap.get(template.id);
      const type = template.type || existingQuestion?.type || QuestionType.TEXT;

      // Initialize default answer based on type if no existing answer
      let defaultAnswer: AdditionalQuestionAnswer;
      switch (type) {
        case QuestionType.BOOLEAN:
          defaultAnswer = undefined;
          break;
        case QuestionType.ARRAY:
          defaultAnswer = [];
          break;
        default:
          defaultAnswer = undefined;
      }

      return {
        id: template.id,
        type,
        answer: existingQuestion?.answer ?? defaultAnswer,
      };
    });
  }
}
